import React, { Suspense, lazy, useEffect, useState } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Routes,
  Route,
  useNavigate,
  Navigate
} from "react-router-dom";
import { AppProvider, Frame } from "@shopify/polaris";
import enTranslations from "@shopify/polaris/locales/en.json";
import { AppProvider as DiscountsProvider } from '@shopify/discount-app-components';
import { NavMenu  } from "@shopify/app-bridge-react";
import { ApolloProvider } from "@apollo/client";
import { createClient } from "./graphql/Client";
import Config from "./utils/Config";
import { SubscriptionPlan } from "./pages/SubscriptionPage/SubscriptionPlan";
import { AdapterLink } from "./utils/ShopifyEmbeddedAppRoute";
import "./App.css";
import "@shopify/polaris/build/esm/styles.css";
import "@shopify/discount-app-components/build/esm/styles.css";
import ExitIFrame from "./components/ExitIFrame/ExitIFrame";
import Loading from "./components/Utils/Loading";
import {
  ChangeSubscriptionRoute,
  UpdatePermissionsRoute,
  WithdrawRoute,
} from "./pages/SubscriptionPage/SubscriptionRoutes";
import DashboardPage from "./pages/DashboardPage/DashboardPage";
import AnnouncementBarPage from "./pages/WidgetsPage/AnnouncementBarPage";
import AccessDeniedPage from "./pages/AccessDeniedPage/AccessDeniedPage";
import { setBeaconStore } from "./utils/BeaconUtils";
//import { setBeaconStore } from "./utils/BeaconUtils";

const graphQlUri = `${Config.apiGateway.URL}/shopifyGraphQL`;
const authQueryString = "?" + window.location.search.split("?")[1];
const urlParams = new URLSearchParams(window.location.search);
const shopOrigin = urlParams.get("shop");
const host = urlParams.get("host");
const locale = urlParams.get("locale");

const client = createClient(graphQlUri, Config.apiGateway.URL, authQueryString, { discoFlyApi: Config.flyApi.URL });

// Pages
const ReviewPage = lazy(() => import("./pages/ReviewPage/ReviewPage"));
const CampaignWizard = lazy(() => import("./pages/CampaignWizardPage/CampaignWizard"));
const VatNumberPage = lazy(() => import("./pages/VatPage/VatNumberPage"));
const ReauthPage = lazy(() => import("./pages/ReauthPage/ReauthPage"));
const SubscriptionPage = lazy(() => import("./pages/SubscriptionPage/SubscriptionPage"));
const FaqPage = lazy(() => import("./pages/FaqPage/FaqPage"));
const WidgetsPage = lazy(() => import("./pages/WidgetsPage/WidgetsPage"));
const CountdownTimerPage = lazy(() => import("./pages/WidgetsPage/CountdownTimerPage"));
const SavingsPage = lazy(() => import("./pages/WidgetsPage/SavingsPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage/SettingsPage"));
//const CampaignsPageLegacy = lazy(() => import("./pages/CampaignsPage/CampaignsPageLegacy"));
const CampaignsPage = lazy(() => import("./pages/CampaignsPage/CampaignsPage"));
const AdvancedToolsPage = lazy(() => import("./pages/AdvancedToolsPage/AdvancedToolsPage"));
const ToolJobWizard = lazy(() => import("./pages/AdvancedToolsPage/ToolJobWizard"));

function App({
  plan = SubscriptionPlan.LegacyStarter,
  freeDiscountsLeft = 0,
  requestVatNumber = false,
  redirectUrl = "",
  missingScopes = [],
  onboardingStatus = {},
  accessDenied = false,
  trialUsedForPlans = {},
  halloweenOffer = null,
}) {
  const navigate = useNavigate();

  useEffect(() => {
    window.$crisp=[];
    window.CRISP_WEBSITE_ID="912570fe-6df6-4689-aa83-0bf215d879e6";
    const script = document.createElement("script");

    script.src = "https://client.crisp.chat/l.js";
    script.async = true;
    

    document.head.appendChild(script);

    setBeaconStore(shopOrigin);
  }, [])

  const [dismissedVatPage] = useState(false);
  const [dismissedReauthPage, setDismissedReauthPage] = useState(false);

  const config = {
    apiKey: Config.apiKey,
    shopOrigin: shopOrigin,
    host: host,
    forceRedirect: false,
    appEmbedUuid: Config.appEmbedUuid,
  };

  return (
    <AppProvider
      i18n={enTranslations}
      linkComponent={AdapterLink}
      features={{ newDesignLanguage: true }}
    >
      <DiscountsProvider locale="en-US" ianaTimezone="America/Los_Angeles">
          <ApolloProvider client={client}>
            {
              !accessDenied &&
                <NavMenu>
                  <a href="/" rel="home">Home</a>
                  <a href="/campaigns">Campaigns</a>
                  <a href="/widgets">Widgets</a>
                  {onboardingStatus?.toolJobsEnabled && <a href="/advanced">Advanced</a>}
                  <a href="/settings">Settings</a>
                  <a href="/subscription">Plan</a>
                  <a href="/faq">FAQ</a>
                </NavMenu>
            }
            {redirectUrl !== "" && <ExitIFrame redirectUrl={redirectUrl} />}
            {redirectUrl === "" && (
              <Suspense fallback={<Frame><Loading /></Frame>}>
                <Routes>
                  <Route
                    path="/dashboard"
                    element={
                      <DashboardPage
                        plan={plan}
                        storeUrl={shopOrigin}
                        locale={locale}
                        appEmbedUuid={config.appEmbedUuid}
                        trialUsedForPlans={trialUsedForPlans}
                        halloweenOffer={halloweenOffer}
                        {...onboardingStatus}
                      />
                    }
                  />
                  <Route
                    path="/access-denied"
                    element={<AccessDeniedPage storeUrl={shopOrigin} />}
                  />
                  <Route path="/review" element={<ReviewPage />} />
                  <Route
                    path="/campaigns/create/:saleType?/:copyId?"
                    element={
                      <CampaignWizard
                        locale={locale}
                        storeUrl={shopOrigin}
                        plan={plan}
                      />
                    }
                  />
                  <Route
                    path="/campaigns/edit/:id"
                    element={
                      <CampaignWizard
                        locale={locale}
                        storeUrl={shopOrigin}
                        plan={plan}
                      />
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <SettingsPage
                        locale={locale}
                        storeUrl={shopOrigin}
                        appName={Config.appName}
                      />
                    }
                  />
                  <Route
                    path="/subscription/:discountCode?"
                    element={
                      <SubscriptionPage
                        locale={locale}
                        storeUrl={shopOrigin}
                        appName={Config.appName}
                        freeDiscountsLeft={freeDiscountsLeft}
                        plan={plan}
                      />
                    }
                  />
                  <Route
                    path="/subscription/change/:targetPlan"
                    element={
                      <ChangeSubscriptionRoute
                        authQueryString={authQueryString}
                        host={window.atob(host)}
                      />
                    }
                  />
                  <Route
                    path="/subscription/withdraw"
                    element={
                      <WithdrawRoute
                        authQueryString={authQueryString}
                      />
                    }
                  />
                  <Route
                    path="/widgets"
                    element={
                      <WidgetsPage
                        locale={locale}
                        storeUrl={shopOrigin}
                        appName={Config.appName}
                        appEmbedUuid={config.appEmbedUuid}
                        {...onboardingStatus}
                      />
                    }
                  />
                  <Route
                    path="/widgets/countdown-timer"
                    element={
                      <CountdownTimerPage 
                        appName={Config.appName} 
                        onBack={() => navigate("/widgets")}
                      />
                    }
                  />
                  <Route
                    path="/widgets/announcement-bar"
                    element={
                      <AnnouncementBarPage 
                        appName={Config.appName} 
                        onBack={() => navigate("/widgets")}
                      />
                    }
                  />
                  <Route
                    path="/widgets/savings"
                    element={
                      <SavingsPage
                        appName={Config.appName}
                        storeUrl={shopOrigin}
                        appEmbedUuid={config.appEmbedUuid}
                        onBack={() => navigate("/widgets")}
                      />
                    }
                  />
                  <Route
                    path="/advanced"
                    element={<AdvancedToolsPage />}
                  />
                  <Route
                    path="/advanced/run/:toolType"
                    element={<ToolJobWizard />}
                  />
                  <Route path="/faq" element={<FaqPage />} />
                  <Route
                    path="/vat"
                    element={<VatNumberPage appName={Config.appName} />}
                  />
                  <Route
                    path="/campaigns"
                    element={
                      <CampaignsPage
                        locale={locale}
                        storeUrl={shopOrigin}
                        plan={plan}
                        showReview={onboardingStatus?.rating !== 5}
                      />
                    }
                  />
                  <Route
                    path="/reauth"
                    element={
                      <ReauthPage
                        laterPressed={dismissedReauthPage}
                        setLaterPressed={setDismissedReauthPage}
                      />
                    }
                  />
                  <Route
                    path="/update-permissions"
                    element={
                      <UpdatePermissionsRoute
                        authQueryString={authQueryString}
                        host={window.atob(host)}
                      />
                    }
                  />
                  <Route
                    path="/"
                    element={
                      <Home
                        accessDenied={accessDenied}
                        requestVatNumber={requestVatNumber}
                        dismissedVatPage={dismissedVatPage}
                        missingScopes={missingScopes}
                      />
                    }
                  />
                </Routes>
              </Suspense>
            )}
          </ApolloProvider>
      </DiscountsProvider>
    </AppProvider>
  );
}

function Home({
  accessDenied = false,
  requestVatNumber = true,
  dismissedVatPage = false,
  missingScopes = [],
}) {
  if (accessDenied) {
    return <Navigate to="/access-denied" />;
  }
  if (requestVatNumber && !dismissedVatPage) {
    return <Navigate to="/vat" />;
  } else if (missingScopes && missingScopes.length > 0) {
    return <Navigate to="/reauth" />;
  } else {
    return <Navigate to="/dashboard" />;
  }
}

function AppWrapper(props) {
  const router = createBrowserRouter([
    {
      path: "*",
      element: <App {...props} />,
    },
  ]);

  return <RouterProvider router={router} />;
}

export default AppWrapper;
